// Lightweight i18n for Santa Maria Legal Advisory.
// English is the base language. Other languages can be added by providing
// the same key structure under en / es / fr / it / pt.
// Translations are provided by the firm — placeholders fall back to English
// when a key is missing in the selected language.

import { createContext, useContext, useEffect, useMemo, useState } from "react";

export const LANGUAGES = [
  { code: "en", label: "EN", name: "English" },
  { code: "es", label: "ES", name: "Español" },
  { code: "fr", label: "FR", name: "Français" },
  { code: "it", label: "IT", name: "Italiano" },
  { code: "pt", label: "PT", name: "Português" },
];

// English is the source of truth.
const en = {
  nav: {
    about: "About",
    expertise: "Expertise",
    founder: "Founder",
    international: "International",
    contact: "Contact",
    cta: "Get in Touch",
  },
  hero: {
    eyebrow: "LONDON-BASED. GLOBALLY CONNECTED.",
    title_l1: "Senior legal advisory",
    title_l2: "built for cross-border business",
    sub: "UK, Europe and the Americas",
    cta_expertise: "Our Expertise",
    cta_contact: "Get in Touch",
  },
  about: {
    eyebrow: "WHO WE ARE",
    headline:
      "An unpredictable world calls for trusted, experienced legal advice that understands your business, anticipates your risks, and turns complexity into decisive action.",
    p1: "Santa Maria Legal Advisory is a London-based specialist firm providing senior-level commercial, regulatory and cross-border legal advisory services to businesses across the UK, Europe and the Americas.",
    p2: "We operate on a flexible advisory model — acting as a trusted strategic partner rather than a traditional law firm, with fee structures designed to give clients access to high-calibre expertise without the overhead of a full-time hire.",
    p3: "Our approach is particularly suited to fast-growing businesses, scale-ups, international companies entering new markets, and established organisations requiring specialist expertise on a project or retainer basis.",
    p4: "For us, the relationship always comes first.",
    pullquote: "\"Cross-border legal insight. Built for business.\"",
  },
  pillars: {
    eyebrow: "WHY SANTA MARIA",
    headline: "Three reasons clients choose us",
  },
  transition: {
    quote: "Cross-border legal insight. Built for business.",
  },
  expertise: {
    eyebrow: "OUR EXPERTISE",
    headline: "Specialist counsel across eight areas of expertise",
  },
  international: {
    eyebrow: "INTERNATIONAL REACH",
    headline: "Local insight. International perspective.",
    lead:
      "Operating across the United Kingdom, Europe, the Americas and selected international markets through a trusted network of legal, commercial and industry professionals.",
  },
  founder: {
    eyebrow: "ABOUT THE FOUNDER",
    title: "Founder & Head of Advisory",
    credentials:
      "Solicitor (England & Wales) · Abogado (Argentina) · Executive MBA, Warwick Business School (UK) · LLM, Queen Mary London (UK, Chevening Scholar) · MSc. Economic Business Law, UCA & Bucerius Law School (Argentina & Germany)",
    p1: "Ezequiel T. Condoluci Santa Maria is the Founder and Head of Advisory at Santa Maria Legal Advisory.",
    p2: "He brings over 18 years of senior international legal experience gained across the UK, Europe, the Americas and globally — built from within global organisations, not from the outside looking in.",
    p3: "At American Express Global Business Travel, he drove and delivered strategic legal initiatives across multiple jurisdictions in Europe and globally. As Head of Legal for Mediterranean Shipping Company Group in South America, he built and ran the legal function across the region. Earlier in his career, he held strategic legal roles at Brödermann Jahn in Germany and Volkswagen in Argentina. Most recently, he served as Legal Director at Hill Dickinson LLP in London, one of the UK's leading international law firms.",
    p4: "This in-house background means he understands what General Counsel and legal teams actually need: clear, commercial advice with no padding, delivered by someone who has sat on the same side of the table.",
    p5: "He is dual-qualified as a Solicitor in England & Wales and as Abogado in Argentina, and advises clients in five languages: English, Spanish, French, Italian and Portuguese. He has advised on transactions and regulatory matters spanning three continents and multiple legal systems.",
    p6: "A practising musician — guitarist, pianist and songwriter — he brings lateral thinking and creative problem-solving to every legal challenge.",
  },
  stats: {
    years: "Years Experience",
    languages: "Languages",
    languages_list: "English, Spanish, French, Italian & Portuguese",
    continents: "Continents",
    value: "Commercial Value Supported",
  },
  contact: {
    headline: "Let's discuss your matter.",
    lead: "Tell us briefly about your situation. We respond within 24 hours.",
    first_name: "FIRST NAME",
    last_name: "LAST NAME",
    email: "EMAIL",
    phone: "PHONE",
    enquiry: "YOUR ENQUIRY",
    submit: "SEND ENQUIRY",
    sending: "SENDING…",
    legal_pre: "By submitting this form you agree to our ",
    legal_link: "Privacy Policy",
    legal_post: ".",
    confirm_title: "Thank you.",
    confirm_body: "We will be in touch within 24 hours.",
    confirm_sub: "A confirmation has been sent to your inbox.",
    send_another: "Send another enquiry",
    err_required: "Please complete all required fields.",
    err_delivery: "We could not deliver your message. Please email info@smlegal.co.uk directly.",
  },
  footer: {
    navigate: "Navigate",
    credentials: "Credentials",
    privacy: "Privacy Policy",
    legal:
      "Santa Maria Legal Advisory Ltd is registered in England and Wales (Company No. 17187611). We provide specialist legal advisory and consultancy services. We are an independent advisory firm and are not authorised or regulated by the Solicitors Regulation Authority. We do not carry out reserved legal activities and do not hold client money. The information on this website does not constitute legal advice or establish a solicitor-client relationship.",
    rights: "All rights reserved",
  },
  cookie: {
    title: "We use only essential cookies",
    body:
      "This site uses strictly necessary cookies required for it to function. We do not use advertising or third-party analytics cookies. See our ",
    privacy: "Privacy Policy",
    accept: "Accept & close",
  },
};

// Placeholders for future translations — fall back to English keys.
const TRANSLATIONS = { en, es: {}, fr: {}, it: {}, pt: {} };

const LangContext = createContext({
  lang: "en",
  setLang: () => {},
  t: (path) => path,
});

const STORAGE_KEY = "sm-lang-v1";

function getNested(obj, path) {
  return path.split(".").reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored && LANGUAGES.find((l) => l.code === stored)) return stored;
    } catch (e) {
      /* ignore */
    }
    return "en";
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
      document.documentElement.lang = lang;
    } catch (e) {
      /* ignore */
    }
  }, [lang]);

  const value = useMemo(
    () => ({
      lang,
      setLang: setLangState,
      t: (path) => {
        const value = getNested(TRANSLATIONS[lang], path);
        if (value !== undefined && value !== "") return value;
        const fallback = getNested(TRANSLATIONS.en, path);
        return fallback !== undefined ? fallback : path;
      },
    }),
    [lang]
  );

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useT() {
  return useContext(LangContext);
}
